import useSWR from "swr"
import { apiGet } from "@/api/client"
import type {
  OverviewData,
  LeadsResponse,
  ApiKeysResponse,
  PublisherProfile,
  StateStats,
  RecentEvent,
  DemandZipsResponse,
} from "@/types"

function fetcher<T>(path: string) {
  return apiGet<T>(path)
}

export function useOverview(startDate?: string, endDate?: string) {
  const params = new URLSearchParams()
  if (startDate) params.set("start_date", startDate)
  if (endDate) params.set("end_date", endDate)
  const qs = params.toString()
  const key = `/dashboard${qs ? `?${qs}` : ""}`

  return useSWR<OverviewData>(key, () => fetcher<OverviewData>(key), {
    refreshInterval: 120_000,
    revalidateOnFocus: false,
  })
}

export function useLeads(
  page: number,
  limit: number,
  filters: { status?: string; startDate?: string; endDate?: string }
) {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("limit", String(limit))
  if (filters.status) params.set("status_filter", filters.status)
  if (filters.startDate) params.set("start_date", filters.startDate)
  if (filters.endDate) params.set("end_date", filters.endDate)
  const key = `/leads?${params.toString()}`

  return useSWR<LeadsResponse>(key, () => fetcher<LeadsResponse>(key), {
    refreshInterval: 120_000,
    revalidateOnFocus: false,
  })
}

export function useLeadSearch(query: string) {
  const key = query.length >= 2 ? `/leads/search?q=${encodeURIComponent(query)}&limit=20` : null

  return useSWR<LeadsResponse>(key, key ? () => fetcher<LeadsResponse>(key) : null, {
    revalidateOnFocus: false,
    dedupingInterval: 500,
  })
}

export function useApiKeys() {
  return useSWR<ApiKeysResponse>("/api-keys", () => fetcher<ApiKeysResponse>("/api-keys"), {
    revalidateOnFocus: false,
  })
}

export function usePublisherProfile() {
  return useSWR<PublisherProfile>("/profile", () => fetcher<PublisherProfile>("/profile"), {
    revalidateOnFocus: false,
  })
}

export function useStateStats(startDate?: string, endDate?: string) {
  const params = new URLSearchParams()
  if (startDate) params.set("start_date", startDate)
  if (endDate) params.set("end_date", endDate)
  const qs = params.toString()
  const key = `/state-stats${qs ? `?${qs}` : ""}`

  return useSWR<{ states: StateStats[] }>(key, () => fetcher<{ states: StateStats[] }>(key), {
    refreshInterval: 120_000,
    revalidateOnFocus: false,
  })
}

export function useDemandZips(page: number, limit: number, search: string) {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("limit", String(limit))
  if (search) params.set("search", search)
  const key = `/demand-zips?${params.toString()}`

  return useSWR<DemandZipsResponse>(key, () => fetcher<DemandZipsResponse>(key), {
    revalidateOnFocus: false,
    keepPreviousData: true,
  })
}

export function fetchDemandZipsExport(search: string) {
  const params = new URLSearchParams()
  if (search) params.set("search", search)
  const qs = params.toString()
  return apiGet<DemandZipsResponse>(`/demand-zips/export${qs ? `?${qs}` : ""}`)
}

export function useRecentActivity() {
  return useSWR<{ events: RecentEvent[] }>(
    "/recent-activity?limit=10",
    () => fetcher<{ events: RecentEvent[] }>("/recent-activity?limit=10"),
    { refreshInterval: 30_000, revalidateOnFocus: false }
  )
}
